import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { datumLang } from "@/i18n/datum";
import { cn } from "@jbnl/shared-ui";
import type { EigenAandeel } from "./eigenKorpsen";
import type { PlanningDag } from "./usePlanning";

/**
 * `Navigatiekaart`'s shape, so the gesture is the one already learnt at the front door. A
 * separate component, because pushing a wedstrijddag's domain into the menu's card would make a
 * shared component carry one screen's subject.
 */
export function Wedstrijddagkaart({
  dag,
  aandeel,
}: {
  dag: PlanningDag;
  aandeel: EigenAandeel;
}) {
  const { t } = useTranslation(["planning"]);

  return (
    // `relative` for the same reason `Navigatiekaart` needs it: the link below stretches an
    // ::after over the whole card, and without a positioned ancestor that overlay sizes itself
    // to the page instead and swallows every click on the grid.
    <Card
      className={cn(
        // `h-full` because the grid stretches the list item but not the card inside it, and a
        // dag whose host name wraps to two lines would otherwise leave its neighbour short.
        "relative flex h-full flex-col transition-colors hover:border-secondary",
        // The mark, and never on its own: the badge below says the same thing in words, so a
        // reader who cannot tell these two borders apart has not lost the answer.
        aandeel.eigen && "border-secondary",
      )}
    >
      <CardHeader>
        <CardTitle className="flex items-start justify-between gap-2">
          <Link
            to="/planning/$dagId"
            params={{ dagId: dag.id }}
            className="after:absolute after:inset-0 focus-visible:outline-none"
          >
            {datumLang(dag.datum)}
          </Link>
          {aandeel.eigen && (
            <Badge className="mt-0.5">{t("planning:eigen.badge")}</Badge>
          )}
        </CardTitle>
        <CardDescription>
          {dag.niveauNaam} ·{" "}
          {dag.organiserendKorpsNaam ?? t("planning:dag.geenKorps")}
        </CardDescription>
      </CardHeader>
      <CardContent className="mt-auto flex items-center justify-between gap-2 text-sm text-muted-foreground">
        {/* A line on every card, so one that says nothing about you is the same height as one
            that does - the grid reads as a row of dagen rather than a ragged edge. */}
        <span className="min-w-0 flex-1">
          <Regel dag={dag} aandeel={aandeel} />
        </span>
        <ChevronRight aria-hidden className="size-4 shrink-0" />
      </CardContent>
    </Card>
  );
}

/**
 * Joined rather than ranked: a korps that both hosts and plays wants both, and that is the
 * arrangement that most changes their day.
 */
function Regel({ dag, aandeel }: { dag: PlanningDag; aandeel: EigenAandeel }) {
  const { t } = useTranslation(["planning"]);

  if (!aandeel.eigen) {
    return <>{t("planning:dag.banen", { count: dag.banen.length })}</>;
  }

  const delen = [
    aandeel.organiseert ? t("planning:eigen.organiseert") : null,
    aandeel.ploegen > 0
      ? t("planning:eigen.ploegen", { count: aandeel.ploegen })
      : null,
  ].filter((deel) => deel !== null);

  return (
    <span className="font-medium text-secondary-active">
      {delen.join(" · ")}
    </span>
  );
}
